import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../../constants/helpRouteMap";
import {
  ContactFieldDefinition,
  ContactFieldType,
  createContactFieldDefinition,
  deleteContactFieldDefinition,
  getContactFieldDefinitions,
  reorderContactFieldDefinitions,
  updateContactFieldDefinition,
} from "../../../../helpers/audience_helper";
import {
  SUGGESTED_CONTACT_FIELD_PACKS,
  packById,
  packIdFromGalleryTag,
  type SuggestedContactFieldPack,
} from "../../../../constants/suggestedContactFieldPacks";
import { getWorkspaceSettings } from "../../../../helpers/workspace_settings_helper";
import { isOwner } from "../../../../utils/tenantRoles";

const PER_PAGE = 30;
const SEARCH_DEBOUNCE_MS = 350;
const FIELD_LIMIT = 30;

const FIELD_TYPES: ContactFieldType[] = [
  "text",
  "textarea",
  "number",
  "date",
  "boolean",
  "select",
  "multiselect",
];

type FormValues = {
  label: string;
  slug: string;
  type: ContactFieldType;
  /** Uma opção por linha — só select/multiselect. */
  optionsText: string;
  is_filterable: boolean;
  is_required: boolean;
  sort_order: number;
};

/** Gera slug estável p/ merge {{ fields.slug }} a partir do rótulo (DOC 03 § 4.7). */
function slugifyLabel(label: string): string {
  const raw = label
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

  if (!raw) {
    return "";
  }
  // API exige começar com letra.
  return /^[a-z]/.test(raw) ? raw : `f_${raw}`;
}

function parseOptionsText(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
}

function mergeToken(slug: string): string {
  return `{{ fields.${slug} }}`;
}

/**
 * Catálogo tipado de campos custom (LAC-071 · DOC 03 § 4.7 / 09).
 * Por quê: Carla define tipos (data, sexo…) antes de filtrar e personalizar e-mails.
 */
const ContactFieldsPage = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || "pt-BR";

  document.title = t("contactFields.documentTitle");

  const [items, setItems] = useState<ContactFieldDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  /** Total sem filtro — usado só para o teto de 30 (DOC 03 § 4.7.3). */
  const [activeTotal, setActiveTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ContactFieldDefinition | null>(null);
  /** Tipo original ao abrir edição — avisa se Carla mudar (valores futuros). */
  const [originalType, setOriginalType] = useState<ContactFieldType | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [reorderingId, setReorderingId] = useState<number | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState<ContactFieldDefinition | null>(null);
  const [deletingBusy, setDeletingBusy] = useState(false);
  /** Usuário editou o slug manualmente — não sobrescrever ao digitar o rótulo. */
  const [slugTouched, setSlugTouched] = useState(false);
  /** Pack ICP em aplicação — evita clique duplo. */
  const [applyingPackId, setApplyingPackId] = useState<string | null>(null);
  /** Confirmação com prévia dos campos antes de criar. */
  const [packToConfirm, setPackToConfirm] =
    useState<SuggestedContactFieldPack | null>(null);
  const [highlightedPackId, setHighlightedPackId] = useState<string | null>(
    null
  );
  const [preferredPackId, setPreferredPackId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const packsSectionRef = useRef<HTMLDivElement | null>(null);
  const owner = isOwner();

  const atLimit = activeTotal >= FIELD_LIMIT;

  const existingSlugs = useMemo(
    () => new Set(items.map((row) => row.slug.toLowerCase())),
    [items]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getContactFieldDefinitions({
        page,
        per_page: PER_PAGE,
        search: appliedSearch || undefined,
      });
      if (!res?.success) {
        setError(t("contactFields.error.load"));
        setItems([]);
        return;
      }
      setItems(res.data || []);
      setTotal(res.meta?.total ?? 0);
      setLastPage(res.meta?.last_page ?? 1);
      if (!appliedSearch) {
        setActiveTotal(res.meta?.total ?? 0);
      }
    } catch (e: any) {
      setError(e?.message || t("contactFields.error.load"));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [page, appliedSearch, t]);

  /**
   * Aplica pack vertical (Educação/SaaS/Publisher) via CRUD existente.
   * Pula slugs que já existem — sem migration.
   */
  const applyPack = useCallback(
    async (pack: SuggestedContactFieldPack) => {
      if (atLimit) {
        toast.error(t("contactFields.packs.atLimit"));
        return;
      }
      setApplyingPackId(pack.id);
      let created = 0;
      let skipped = 0;
      try {
        for (const field of pack.fields) {
          if (existingSlugs.has(field.slug.toLowerCase())) {
            skipped += 1;
            continue;
          }
          if (activeTotal + created >= FIELD_LIMIT) {
            toast.error(t("contactFields.packs.atLimit"));
            break;
          }
          const res = await createContactFieldDefinition({
            label: t(field.labelKey),
            slug: field.slug,
            type: field.type,
            is_filterable: field.is_filterable,
            is_required: false,
            sort_order: activeTotal + created,
          });
          if (!res?.success) {
            throw new Error(t("contactFields.error.save"));
          }
          created += 1;
        }
        if (created > 0) {
          toast.success(
            t("contactFields.packs.applied", { count: created, skipped })
          );
          setPackToConfirm(null);
          await load();
        } else if (skipped > 0) {
          toast.info(t("contactFields.packs.allExist"));
          setPackToConfirm(null);
        }
      } catch (e: any) {
        toast.error(e?.message || t("contactFields.error.save"));
      } finally {
        setApplyingPackId(null);
      }
    },
    [atLimit, existingSlugs, activeTotal, t, load]
  );

  const openPackConfirm = useCallback((pack: SuggestedContactFieldPack) => {
    setHighlightedPackId(pack.id);
    setPackToConfirm(pack);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Preferência de galeria (Educação/SaaS/Publisher) → sugere o pack. */
  useEffect(() => {
    if (!owner) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await getWorkspaceSettings();
        if (cancelled) {
          return;
        }
        const packId = packIdFromGalleryTag(res.data?.preferred_gallery_tag);
        setPreferredPackId(packId);
      } catch {
        // Preferência é opcional — não bloquear a tela de campos.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [owner]);

  /** Deep-link `?pack=educacao|saas|publisher` — destaca e abre prévia. */
  useEffect(() => {
    const raw = (searchParams.get("pack") || "").toLowerCase();
    const pack = packById(raw);
    if (!pack) {
      return;
    }
    setHighlightedPackId(pack.id);
    setPackToConfirm(pack);
    packsSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    const next = new URLSearchParams(searchParams);
    next.delete("pack");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = search.trim();
      setAppliedSearch((prev) => {
        if (prev === next) {
          return prev;
        }
        setPage(1);
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        label: Yup.string()
          .trim()
          .required(t("contactFields.form.labelRequired"))
          .max(100, t("contactFields.form.labelMax")),
        slug: Yup.string()
          .trim()
          .required(t("contactFields.form.slugRequired"))
          .matches(
            /^[a-z][a-z0-9]*(?:[_-][a-z0-9]+)*$/,
            t("contactFields.form.slugInvalid")
          )
          .max(64),
        type: Yup.mixed<ContactFieldType>()
          .oneOf(FIELD_TYPES)
          .required(t("contactFields.form.typeRequired")),
        optionsText: Yup.string().when("type", {
          is: (type: ContactFieldType) =>
            type === "select" || type === "multiselect",
          then: (schema) =>
            schema.test(
              "options-required",
              t("contactFields.form.optionsRequired"),
              (value) => parseOptionsText(value || "").length > 0
            ),
          otherwise: (schema) => schema.optional(),
        }),
        is_filterable: Yup.boolean(),
        is_required: Yup.boolean(),
        sort_order: Yup.number().min(0).max(9999),
      }),
    [t]
  );

  const formik = useFormik<FormValues>({
    initialValues: {
      label: "",
      slug: "",
      type: "text",
      optionsText: "",
      is_filterable: true,
      is_required: false,
      sort_order: 0,
    },
    validationSchema,
    onSubmit: async (values, helpers) => {
      setSaving(true);
      setError(null);
      const needsOptions =
        values.type === "select" || values.type === "multiselect";
      const options = needsOptions ? parseOptionsText(values.optionsText) : null;

      try {
        if (editing) {
          const res = await updateContactFieldDefinition(editing.id, {
            label: values.label.trim(),
            type: values.type,
            options,
            is_filterable: values.is_filterable,
            is_required: values.is_required,
            sort_order: Number(values.sort_order) || 0,
          });
          if (!res?.success) {
            helpers.setStatus(t("contactFields.error.save"));
            return;
          }
          toast.success(res.message || t("contactFields.toast.updated"), {
            autoClose: 2500,
          });
        } else {
          const res = await createContactFieldDefinition({
            slug: values.slug.trim(),
            label: values.label.trim(),
            type: values.type,
            options,
            is_filterable: values.is_filterable,
            is_required: values.is_required,
            sort_order: Number(values.sort_order) || 0,
          });
          if (!res?.success) {
            helpers.setStatus(t("contactFields.error.save"));
            return;
          }
          toast.success(res.message || t("contactFields.toast.created"), {
            autoClose: 2500,
          });
        }
        setModalOpen(false);
        setEditing(null);
        setOriginalType(null);
        helpers.resetForm();
        setSlugTouched(false);
        setPage(1);
        await load();
      } catch (err: any) {
        // Limite / type imutável: message i18n da API — nunca error.code cru (DOC 13).
        if (err?.code === "FIELD_DEFINITION_LIMIT") {
          helpers.setStatus(t("contactFields.error.limit"));
          return;
        }
        if (err?.code === "FIELD_TYPE_IMMUTABLE_WITH_VALUES") {
          helpers.setStatus(
            err?.message || t("contactFields.error.typeImmutable")
          );
          return;
        }
        const details = err?.details || err?.response?.data?.error?.details;
        if (details?.slug?.[0]) {
          helpers.setFieldError("slug", details.slug[0]);
        } else if (details?.label?.[0]) {
          helpers.setFieldError("label", details.label[0]);
        } else if (details?.options?.[0]) {
          helpers.setFieldError("optionsText", details.options[0]);
        } else if (details?.type?.[0]) {
          helpers.setFieldError("type", details.type[0]);
        } else {
          helpers.setStatus(err?.message || t("contactFields.error.save"));
        }
      } finally {
        setSaving(false);
      }
    },
  });

  const openCreate = () => {
    if (atLimit) {
      setError(t("contactFields.error.limit"));
      return;
    }
    setEditing(null);
    setOriginalType(null);
    setSlugTouched(false);
    formik.resetForm({
      values: {
        label: "",
        slug: "",
        type: "text",
        optionsText: "",
        is_filterable: true,
        is_required: false,
        sort_order: 0,
      },
    });
    setModalOpen(true);
  };

  const openEdit = (row: ContactFieldDefinition) => {
    setEditing(row);
    setOriginalType(row.type);
    setSlugTouched(true);
    formik.resetForm({
      values: {
        label: row.label,
        slug: row.slug,
        type: row.type,
        optionsText: (row.options || []).join("\n"),
        is_filterable: row.is_filterable,
        is_required: row.is_required,
        sort_order: row.sort_order,
      },
    });
    setModalOpen(true);
  };

  const copyMergeToken = async (slug: string) => {
    const token = mergeToken(slug);
    try {
      await navigator.clipboard.writeText(token);
      toast.success(t("contactFields.toast.copied", { token }), {
        autoClose: 2000,
      });
    } catch {
      toast.error(t("contactFields.error.copy"), { autoClose: 2500 });
    }
  };

  /** Sobe/desce na lista trocando sort_order com o vizinho (fallback sem DnD). */
  const moveRow = async (row: ContactFieldDefinition, direction: -1 | 1) => {
    if (appliedSearch !== "" || reorderingId !== null) {
      return;
    }
    const sorted = [...items].sort(
      (a, b) => a.sort_order - b.sort_order || a.id - b.id
    );
    const index = sorted.findIndex((item) => item.id === row.id);
    const swapWith = sorted[index + direction];
    if (!swapWith) {
      return;
    }
    setReorderingId(row.id);
    try {
      await Promise.all([
        updateContactFieldDefinition(row.id, {
          sort_order: swapWith.sort_order,
        }),
        updateContactFieldDefinition(swapWith.id, {
          sort_order: row.sort_order,
        }),
      ]);
      await load();
    } catch (err: any) {
      setError(err?.message || t("contactFields.error.reorder"));
    } finally {
      setReorderingId(null);
    }
  };

  /** HTML5 DnD — persiste ordem via API reorder (LAC-080). */
  const persistOrder = async (ordered: ContactFieldDefinition[]) => {
    setReorderingId(-1);
    try {
      await reorderContactFieldDefinitions({
        ordered_ids: ordered.map((row) => row.id),
      });
      await load();
    } catch (err: any) {
      setError(err?.message || t("contactFields.error.reorder"));
    } finally {
      setReorderingId(null);
      setDraggingId(null);
    }
  };

  const handleDropOnRow = (targetId: number) => {
    if (draggingId === null || draggingId === targetId || appliedSearch !== "") {
      return;
    }
    const ordered = [...sortedForOrder];
    const fromIndex = ordered.findIndex((row) => row.id === draggingId);
    const toIndex = ordered.findIndex((row) => row.id === targetId);
    if (fromIndex < 0 || toIndex < 0) {
      return;
    }
    const [moved] = ordered.splice(fromIndex, 1);
    ordered.splice(toIndex, 0, moved);
    void persistOrder(ordered);
  };

  const confirmDelete = async () => {
    if (!deleting) {
      return;
    }
    setDeletingBusy(true);
    try {
      const res = await deleteContactFieldDefinition(deleting.id);
      toast.success(res?.message || t("contactFields.toast.deleted"), {
        autoClose: 2500,
      });
      setDeleting(null);
      await load();
    } catch (err: any) {
      setError(err?.message || t("contactFields.error.delete"));
      setDeleting(null);
    } finally {
      setDeletingBusy(false);
    }
  };

  const onLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const label = e.target.value;
    formik.setFieldValue("label", label);
    if (!editing && !slugTouched) {
      formik.setFieldValue("slug", slugifyLabel(label));
    }
  };

  const needsOptions =
    formik.values.type === "select" || formik.values.type === "multiselect";
  const optionsPreview = needsOptions
    ? parseOptionsText(formik.values.optionsText)
    : [];
  const typeChangedOnEdit =
    !!editing &&
    originalType !== null &&
    formik.values.type !== originalType;
  const hasFilters = appliedSearch !== "";
  const helpHref = helpUrlForRoute("/settings/contact-fields", locale);
  const sortedForOrder = useMemo(
    () =>
      [...items].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id),
    [items]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={t("contactFields.title")}
          actions={
            <PageHelpLink href={helpHref} label={t("contactFields.help")} />
          }
        />
        <ToastContainer />

        {error && (
          <Alert color="danger" toggle={() => setError(null)}>
            {error}
          </Alert>
        )}

        <DismissiblePageTip tipId="contact-fields">
          {t("contactFields.tip")}
        </DismissiblePageTip>

        {preferredPackId && (
          <Alert color="info" className="mb-3 py-2">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span>
                {t("contactFields.packs.preferredHint", {
                  tag: t(`platformTemplates.tag.${preferredPackId}`, {
                    defaultValue: preferredPackId,
                  }),
                })}
              </span>
              <Button
                color="info"
                outline
                size="sm"
                disabled={atLimit || applyingPackId !== null}
                onClick={() => {
                  const pack = packById(preferredPackId);
                  if (pack) {
                    openPackConfirm(pack);
                  }
                }}
              >
                {t("contactFields.packs.preferredCta")}
              </Button>
            </div>
          </Alert>
        )}

        {/* Packs ICP — prévia + confirmação; CRUD continua sendo a fonte da verdade. */}
        <div ref={packsSectionRef}>
          <Card className="mb-3">
            <CardBody className="py-3">
              <h6 className="mb-1">{t("contactFields.packs.title")}</h6>
              <p className="text-muted small mb-3 mb-md-2">
                {t("contactFields.packs.subtitle")}
              </p>
              <Row className="g-2">
                {SUGGESTED_CONTACT_FIELD_PACKS.map((pack) => {
                  const isHighlight = highlightedPackId === pack.id;
                  return (
                    <Col md={4} key={pack.id}>
                      <div
                        id={`contact-field-pack-${pack.id}`}
                        className={`border rounded p-3 h-100 d-flex flex-column${
                          isHighlight ? " border-primary shadow-sm" : ""
                        }`}
                      >
                        <strong className="mb-1">{t(pack.titleKey)}</strong>
                        <p className="text-muted small mb-2">
                          {t(pack.descriptionKey)}
                        </p>
                        <ul className="small text-muted mb-3 ps-3 flex-grow-1">
                          {pack.fields.map((field) => (
                            <li key={field.slug}>
                              {t(field.labelKey)}{" "}
                              <code className="small">{`{{ fields.${field.slug} }}`}</code>
                              {existingSlugs.has(field.slug) && (
                                <Badge color="light" className="ms-1 text-muted">
                                  {t("contactFields.packs.alreadyExists")}
                                </Badge>
                              )}
                            </li>
                          ))}
                        </ul>
                        <Button
                          color={isHighlight ? "primary" : "light"}
                          size="sm"
                          disabled={atLimit || applyingPackId !== null}
                          onClick={() => openPackConfirm(pack)}
                        >
                          {t("contactFields.packs.previewApply")}
                        </Button>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            </CardBody>
          </Card>
        </div>

        <Modal
          isOpen={packToConfirm !== null}
          toggle={() => setPackToConfirm(null)}
          centered
        >
          <ModalHeader toggle={() => setPackToConfirm(null)}>
            {packToConfirm
              ? t(packToConfirm.titleKey)
              : t("contactFields.packs.title")}
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">{t("contactFields.packs.confirmIntro")}</p>
            {packToConfirm && (
              <ul className="mb-0">
                {packToConfirm.fields.map((field) => (
                  <li key={field.slug}>
                    <strong>{t(field.labelKey)}</strong>{" "}
                    <span className="text-muted">
                      ({field.slug}
                      {existingSlugs.has(field.slug)
                        ? ` — ${t("contactFields.packs.alreadyExists")}`
                        : ""}
                      )
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={() => setPackToConfirm(null)}>
              {t("contactFields.form.cancel")}
            </Button>
            <Button
              color="primary"
              disabled={
                !packToConfirm || atLimit || applyingPackId !== null
              }
              onClick={() => {
                if (packToConfirm) {
                  void applyPack(packToConfirm);
                }
              }}
            >
              {applyingPackId && <Spinner size="sm" className="me-1" />}
              {t("contactFields.packs.apply")}
            </Button>
          </ModalFooter>
        </Modal>

        {atLimit && (
          <Alert color="warning" className="mb-3">
            {t("contactFields.error.limit")}
          </Alert>
        )}

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-start">
                  <div>
                    <h5 className="card-title mb-1">
                      {t("contactFields.title")}
                    </h5>
                    <p className="text-muted mb-0">
                      {t("contactFields.subtitle")}
                    </p>
                  </div>
                  <Button
                    color="primary"
                    onClick={openCreate}
                    disabled={atLimit}
                  >
                    {t("contactFields.add")}
                  </Button>
                </div>
              </CardHeader>
              <CardBody>
                <Row className="g-2 mb-3">
                  <Col md={5}>
                    <Input
                      type="search"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={t("contactFields.search.placeholder")}
                      aria-label={t("contactFields.search.placeholder")}
                    />
                  </Col>
                  {hasFilters && (
                    <Col md="auto">
                      <Button
                        color="light"
                        onClick={() => {
                          setSearch("");
                          setAppliedSearch("");
                          setPage(1);
                        }}
                      >
                        {t("contactFields.filter.clear")}
                      </Button>
                    </Col>
                  )}
                </Row>

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" />
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <div className="avatar-md mx-auto mb-3">
                      <div className="avatar-title bg-light text-primary rounded-circle fs-2">
                        <i className="ri-input-field" />
                      </div>
                    </div>
                    <h5>
                      {hasFilters
                        ? t("contactFields.empty.filteredTitle")
                        : t("contactFields.empty.title")}
                    </h5>
                    <p className="mb-3">
                      {hasFilters
                        ? t("contactFields.empty.filteredDescription")
                        : t("contactFields.empty.description")}
                    </p>
                    {!hasFilters && (
                      <Button
                        color="primary"
                        onClick={openCreate}
                        disabled={atLimit}
                      >
                        {t("contactFields.empty.cta")}
                      </Button>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-muted mb-2">
                      {t("contactFields.totalCount", {
                        count: hasFilters ? total : activeTotal,
                        max: FIELD_LIMIT,
                      })}
                    </p>
                    <div className="table-responsive">
                      <Table className="align-middle table-nowrap mb-0">
                        <thead className="table-light">
                          <tr>
                            {!hasFilters && (
                              <th style={{ width: 72 }}>
                                {t("contactFields.table.order")}
                              </th>
                            )}
                            <th>{t("contactFields.table.label")}</th>
                            <th>{t("contactFields.table.slug")}</th>
                            <th>{t("contactFields.table.type")}</th>
                            <th>{t("contactFields.table.flags")}</th>
                            <th style={{ width: 140 }} />
                          </tr>
                        </thead>
                        <tbody>
                          {sortedForOrder.map((row, index) => (
                            <tr
                              key={row.id}
                              draggable={!hasFilters && reorderingId === null}
                              onDragStart={() => setDraggingId(row.id)}
                              onDragEnd={() => setDraggingId(null)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={() => handleDropOnRow(row.id)}
                              className={
                                draggingId === row.id ? "table-active" : undefined
                              }
                            >
                              {!hasFilters && (
                                <td>
                                  <div className="d-flex flex-column gap-1">
                                    <Button
                                      color="soft-secondary"
                                      size="sm"
                                      className="btn-icon"
                                      disabled={
                                        index === 0 || reorderingId !== null
                                      }
                                      onClick={() => moveRow(row, -1)}
                                      aria-label={t("contactFields.reorder.up")}
                                    >
                                      {reorderingId === row.id ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <i className="ri-arrow-up-s-line" />
                                      )}
                                    </Button>
                                    <Button
                                      color="soft-secondary"
                                      size="sm"
                                      className="btn-icon"
                                      disabled={
                                        index === sortedForOrder.length - 1 ||
                                        reorderingId !== null
                                      }
                                      onClick={() => moveRow(row, 1)}
                                      aria-label={t(
                                        "contactFields.reorder.down"
                                      )}
                                    >
                                      <i className="ri-arrow-down-s-line" />
                                    </Button>
                                  </div>
                                </td>
                              )}
                              <td className="fw-medium">{row.label}</td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-link btn-sm p-0 text-start text-decoration-none"
                                  onClick={() => copyMergeToken(row.slug)}
                                  title={t("contactFields.copyToken")}
                                  aria-label={t("contactFields.copyToken")}
                                >
                                  <code className="text-muted">
                                    {mergeToken(row.slug)}
                                  </code>
                                  <i className="ri-file-copy-line ms-1 align-middle" />
                                </button>
                              </td>
                              <td>
                                <Badge color="secondary" pill>
                                  {t(`contactFields.type.${row.type}`)}
                                </Badge>
                              </td>
                              <td className="text-muted small">
                                {[
                                  row.is_filterable
                                    ? t("contactFields.flag.filterable")
                                    : null,
                                  row.is_required
                                    ? t("contactFields.flag.required")
                                    : null,
                                ]
                                  .filter(Boolean)
                                  .join(" · ") || "—"}
                              </td>
                              <td>
                                <div className="d-flex gap-1">
                                  <Button
                                    color="soft-primary"
                                    size="sm"
                                    onClick={() => openEdit(row)}
                                    aria-label={t("contactFields.edit")}
                                  >
                                    <i className="ri-pencil-line" />
                                  </Button>
                                  <Button
                                    color="soft-danger"
                                    size="sm"
                                    onClick={() => setDeleting(row)}
                                    aria-label={t(
                                      "contactFields.delete.button"
                                    )}
                                  >
                                    <i className="ri-delete-bin-line" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>

                    {lastPage > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <span className="text-muted">
                          {t("contactFields.pagination.label", {
                            page,
                            lastPage,
                            total,
                          })}
                        </span>
                        <div className="d-flex gap-2">
                          <Button
                            color="light"
                            size="sm"
                            disabled={page <= 1}
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                          >
                            {t("contactFields.pagination.prev")}
                          </Button>
                          <Button
                            color="light"
                            size="sm"
                            disabled={page >= lastPage}
                            onClick={() =>
                              setPage((p) => Math.min(lastPage, p + 1))
                            }
                          >
                            {t("contactFields.pagination.next")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={modalOpen}
        toggle={() => !saving && setModalOpen(false)}
        centered
        size="lg"
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
        >
          <ModalHeader toggle={() => !saving && setModalOpen(false)}>
            {editing
              ? t("contactFields.form.editTitle")
              : t("contactFields.form.createTitle")}
          </ModalHeader>
          <ModalBody>
            <p className="text-muted small mb-3">
              {t("contactFields.form.help")}
            </p>
            {formik.status && (
              <Alert color="danger" className="py-2">
                {formik.status}
              </Alert>
            )}
            <div className="mb-3">
              <Label htmlFor="cf-label">{t("contactFields.form.label")}</Label>
              <Input
                id="cf-label"
                name="label"
                value={formik.values.label}
                onChange={onLabelChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.label && formik.errors.label)}
                placeholder={t("contactFields.form.labelPlaceholder")}
              />
              {formik.touched.label && formik.errors.label && (
                <FormFeedback>{formik.errors.label}</FormFeedback>
              )}
            </div>
            <div className="mb-3">
              <Label htmlFor="cf-slug">{t("contactFields.form.slug")}</Label>
              <div className="input-group">
                <Input
                  id="cf-slug"
                  name="slug"
                  value={formik.values.slug}
                  disabled={!!editing}
                  onChange={(e) => {
                    setSlugTouched(true);
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  invalid={!!(formik.touched.slug && formik.errors.slug)}
                  placeholder={t("contactFields.form.slugPlaceholder")}
                />
                {formik.values.slug && (
                  <Button
                    type="button"
                    color="light"
                    outline
                    onClick={() => copyMergeToken(formik.values.slug)}
                    title={t("contactFields.copyToken")}
                    aria-label={t("contactFields.copyToken")}
                  >
                    <i className="ri-file-copy-line" />
                  </Button>
                )}
              </div>
              {formik.touched.slug && formik.errors.slug && (
                <FormFeedback className="d-block">
                  {formik.errors.slug}
                </FormFeedback>
              )}
              <div className="form-text">
                {editing
                  ? t("contactFields.form.slugImmutable")
                  : t("contactFields.form.slugHint")}
                {formik.values.slug && (
                  <>
                    {" "}
                    <code className="small">{mergeToken(formik.values.slug)}</code>
                  </>
                )}
              </div>
            </div>
            <div className="mb-3">
              <Label htmlFor="cf-type">{t("contactFields.form.type")}</Label>
              <Input
                id="cf-type"
                type="select"
                name="type"
                value={formik.values.type}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                invalid={!!(formik.touched.type && formik.errors.type)}
              >
                {FIELD_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {t(`contactFields.type.${type}`)}
                  </option>
                ))}
              </Input>
              {formik.touched.type && formik.errors.type && (
                <FormFeedback>{formik.errors.type}</FormFeedback>
              )}
              {typeChangedOnEdit && (
                <Alert color="warning" className="py-2 mt-2 mb-0">
                  {t("contactFields.form.typeChangeWarning")}
                </Alert>
              )}
            </div>
            {needsOptions && (
              <div className="mb-3">
                <Label htmlFor="cf-options">
                  {t("contactFields.form.options")}
                </Label>
                <Input
                  id="cf-options"
                  type="textarea"
                  name="optionsText"
                  rows={4}
                  value={formik.values.optionsText}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={
                    !!(formik.touched.optionsText && formik.errors.optionsText)
                  }
                  placeholder={t("contactFields.form.optionsPlaceholder")}
                />
                {formik.touched.optionsText && formik.errors.optionsText && (
                  <FormFeedback>{formik.errors.optionsText}</FormFeedback>
                )}
                <div className="form-text">
                  {t("contactFields.form.optionsHint")}
                </div>
                {optionsPreview.length > 0 && (
                  <div className="mt-2 p-2 border rounded bg-light">
                    <div className="small text-muted mb-1">
                      {t("contactFields.form.optionsPreview")}
                    </div>
                    <div className="d-flex flex-wrap gap-1 mb-2">
                      {optionsPreview.map((opt) => (
                        <Badge key={opt} color="info" pill>
                          {opt}
                        </Badge>
                      ))}
                    </div>
                    <Input
                      type="select"
                      bsSize="sm"
                      disabled
                      defaultValue=""
                      aria-hidden
                    >
                      <option value="">
                        {formik.values.type === "multiselect"
                          ? t("contactFields.form.optionsPreviewMulti")
                          : t("contactFields.form.optionsPreviewSingle")}
                      </option>
                      {optionsPreview.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}
              </div>
            )}
            <Row className="g-3">
              <Col md={4}>
                <div className="form-check form-switch">
                  <Input
                    id="cf-filterable"
                    type="switch"
                    name="is_filterable"
                    checked={formik.values.is_filterable}
                    onChange={formik.handleChange}
                  />
                  <Label className="form-check-label" htmlFor="cf-filterable">
                    {t("contactFields.form.isFilterable")}
                  </Label>
                </div>
              </Col>
              <Col md={4}>
                <div className="form-check form-switch">
                  <Input
                    id="cf-required"
                    type="switch"
                    name="is_required"
                    checked={formik.values.is_required}
                    onChange={formik.handleChange}
                  />
                  <Label className="form-check-label" htmlFor="cf-required">
                    {t("contactFields.form.isRequired")}
                  </Label>
                </div>
              </Col>
              <Col md={4}>
                <Label htmlFor="cf-sort">{t("contactFields.form.sortOrder")}</Label>
                <Input
                  id="cf-sort"
                  type="number"
                  name="sort_order"
                  min={0}
                  value={formik.values.sort_order}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              type="button"
              color="light"
              disabled={saving}
              onClick={() => setModalOpen(false)}
            >
              {t("contactFields.form.cancel")}
            </Button>
            <Button type="submit" color="primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  {t("contactFields.form.saving")}
                </>
              ) : (
                t("contactFields.form.save")
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      <Modal
        isOpen={!!deleting}
        toggle={() => !deletingBusy && setDeleting(null)}
        centered
      >
        <ModalHeader toggle={() => !deletingBusy && setDeleting(null)}>
          {t("contactFields.delete.title")}
        </ModalHeader>
        <ModalBody>
          <p className="mb-2">
            {t("contactFields.delete.confirmBefore")}{" "}
            <strong className="text-danger">{deleting?.label}</strong>
            {t("contactFields.delete.confirmAfter")}
          </p>
          <p className="text-muted mb-0">
            {t("contactFields.delete.consequence")}
          </p>
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={deletingBusy}
            onClick={() => setDeleting(null)}
          >
            {t("contactFields.form.cancel")}
          </Button>
          <Button
            color="danger"
            disabled={deletingBusy}
            onClick={confirmDelete}
          >
            {deletingBusy ? (
              <Spinner size="sm" />
            ) : (
              t("contactFields.delete.confirmButton")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default ContactFieldsPage;
